/// <reference path="../../env.d.ts" />
import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  IconButton,
  MenuItem,
  Paper,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEditSite } from "../../utils/api/editSite.ts";
import { useAuth } from "../../context/auth.context.tsx";
import { useNotification } from "../../context/notification.context.tsx";
import { usePortfolios } from "../../utils/api/usePortfolios.ts";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import { useSite } from "../../utils/api/useSites.ts";
import { useSiteGeometry } from "../../utils/api/useSiteGeometry.ts";
import { useSiteGeometryGeoJSON } from "../../utils/api/useSiteGeometryGeoJSON.ts";
import PolygonMap from "../PolygonMap/PolygonMap.tsx";
import { isGeoJson, geojsonToWkt, normalizePolygonString } from "../../utils/polygonUtils.ts";
import { useEditSiteGeometry } from "../../utils/api/editSiteGeometry.ts";
import CustomButton from "../Basics/CustomButton/CustomButton.tsx";
import {
  validateSiteForm,
  parseBackendValidationErrors,
  toUserFriendlyPolygonError,
} from "../../utils/siteFormUtils.ts";

const REQUIRED_FIELDS = ["name", "portfolio", "dc_kw", "ac_kw"] as const;
const NUMERIC_FIELDS = ["dc_kw", "ac_kw"];

/** Wider edit dialog; map column sizes are inline below */
const EDIT_SITE_MODAL_MAX_WIDTH = 1200;
const EDIT_SITE_MODAL_VH = 90;
/** Used in calc(...) so the two-column area fits under the sticky header */
const EDIT_SITE_BODY_BELOW_HEADER_PX = 80;
/** Slightly tighter cap for the scrollable form column */
const EDIT_SITE_FORM_SCROLL_TOP_PX = 72;

interface EditNewSiteModalProps {
  open: boolean;
  onClose: () => void;
  id: any;
}

const EditNewSiteModal: React.FC<EditNewSiteModalProps> = ({
  open,
  onClose,
  id,
}) => {
  const theme = useTheme();
  const isWideLayout = useMediaQuery(theme.breakpoints.up("md"));

  const { mutate: editSite, isPending } = useEditSite();
  const { mutate: editSiteGeometry } = useEditSiteGeometry();
  const { session } = useAuth();
  const { addNotification } = useNotification();

  const { data: PortfoliosData, isLoading: isLoadingPortfoliosData } =
    usePortfolios(session);

  const {
    data: siteData,
    isLoading: isLoadingSiteData,
    refetch: refetchSiteData,
  } = useSite(session, id);

  const {
    data: siteGeometry,
    isLoading: isLoadingSiteGeometry,
    refetch: refetchSiteGeometry,
  } = useSiteGeometry(session, siteData?.sitegeometry);

  const { data: siteGeometryGeoJSON } = useSiteGeometryGeoJSON(
    session,
    siteGeometry?.id
  );

  // Form data state
  const [formData, setFormData] = useState<any>({
    sam_id: "",
    name: "",
    city: "",
    state: "",
    address: "",
    latitude: "",
    longitude: "",
    portfolio: "",
    docket_id: "",
    dc_kw: "",
    ac_kw: "",
    polygon: "",
    ac_source: "",
    dc_source: "",
  });

  /** Initial polygon as WKT (avoids GeoJSON vs WKT mismatch) */
  const [initialPolygonWkt, setInitialPolygonWkt] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Completely reset the modal state when it closes
  useEffect(() => {
    if (!open) {
      setFormData({
        sam_id: "",
        name: "",
        city: "",
        state: "",
        address: "",
        latitude: "",
        longitude: "",
        portfolio: "",
        docket_id: "",
        dc_kw: "",
        ac_kw: "",
        polygon: "",
        ac_source: "",
        dc_source: "",
      });
      setInitialPolygonWkt("");
      setFormErrors({});
      refetchSiteData();
      refetchSiteGeometry();
    }
  }, [open, refetchSiteData, refetchSiteGeometry]);

  // Fetch fresh data when the modal opens and `id` changes
  useEffect(() => {
    if (open) {
      refetchSiteData(); // Force refetch the site data
      refetchSiteGeometry(); // Force refetch the site geometry
    }
  }, [open, id, refetchSiteData, refetchSiteGeometry]);

  // Update form data once site data and geometry are loaded. Prefer GeoJSON when available 
  useEffect(() => {
    if (
      open &&
      siteData &&
      siteGeometry &&
      !isLoadingSiteData &&
      !isLoadingSiteGeometry
    ) {
      const polygonValue =
        siteGeometryGeoJSON && typeof siteGeometryGeoJSON === "object"
          ? JSON.stringify(siteGeometryGeoJSON, null, 2)
          : normalizePolygonString(siteGeometry.poly || "");

      const polygonWktForCompare =
        siteGeometryGeoJSON && typeof siteGeometryGeoJSON === "object"
          ? geojsonToWkt(siteGeometryGeoJSON)
          : normalizePolygonString(siteGeometry.poly || "");

      setFormData({
        sam_id: siteData.sam_id || "",
        name: siteData.name || "",
        city: siteData.city || "",
        state: siteData.state || "",
        address: siteData.address || "",
        latitude: siteData.latitude || "",
        longitude: siteData.longitude || "",
        portfolio: siteData.portfolio || "",
        docket_id: siteData.docket_id || "",
        dc_kw: siteData.dc_kw || "",
        ac_kw: siteData.ac_kw || "",
        polygon: polygonValue,
        ac_source: siteData.ac_source || "",
        dc_source: siteData.dc_source || "",
      });
      setInitialPolygonWkt(polygonWktForCompare);
    }
  }, [
    open,
    siteData,
    siteGeometry,
    siteGeometryGeoJSON,
    isLoadingSiteData,
    isLoadingSiteGeometry,
  ]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "dc_kw" || name === "ac_kw" ? parseFloat(value) || "" : value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = () => {
    const errors = validateSiteForm(formData, REQUIRED_FIELDS, NUMERIC_FIELDS);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const polygonValue = formData.polygon || "";
    let polygonWkt: string;
    if (isGeoJson(polygonValue)) {
      try {
        const parsed = JSON.parse(polygonValue.trim());
        polygonWkt = geojsonToWkt(parsed);
        if (!polygonWkt) {
          setFormErrors((prev) => ({
            ...prev,
            polygon: "Invalid GeoJSON: could not convert to polygon",
          }));
          return;
        }
      } catch {
        setFormErrors((prev) => ({ ...prev, polygon: "Invalid GeoJSON" }));
        return;
      }
    } else {
      polygonWkt = polygonValue;
    }

    setFormErrors({});
    setIsSubmitting(true);

    const payload = { ...formData };
    payload.portfolio = `${process.env.REACT_APP_API_BASE_URL}portfolios/${formData.portfolio}/`;
    payload.dc_source = (payload.dc_source || "").replace(/ /g, "_");
    payload.ac_source = (payload.ac_source || "").replace(/ /g, "_");
    payload.polygon = polygonWkt;

    editSite(
      { ...payload, id },
      {
        onSuccess: () => {
          const polygonChanged = polygonWkt !== initialPolygonWkt;

          if (polygonChanged && siteGeometry && payload.polygon) {
            const siteGeometryData = {
              ...siteGeometry,
              poly: payload.polygon,
              id: siteGeometry.id,
            };

            editSiteGeometry(siteGeometryData, {
              onSuccess: () => {
                addNotification("The site was edited successfully", true);
                onClose();
              },
              onError: (error) => {
                const errs = parseBackendValidationErrors(error);
                if (errs !== null) {
                  const rawMsg =
                    errs.polygon ??
                    errs.detail ??
                    errs.non_field_errors ??
                    (Object.keys(errs).length > 0
                      ? Object.values(errs)[0]
                      : "Invalid polygon");
                  const polygonMsg = toUserFriendlyPolygonError(rawMsg);
                  setFormErrors((prev) => ({ ...prev, polygon: polygonMsg }));
                } else {
                  console.error("Failed to edit site geometry", error);
                  addNotification("Failed to edit the site geometry", false);
                }
                setIsSubmitting(false);
              },
            });
          } else {
            addNotification("The site was edited successfully", true);
            onClose();
          }
        },
        onError: (error) => {
          const errs = parseBackendValidationErrors(error);
          if (errs) {
            setFormErrors(errs);
          } else {
            console.error("Failed to edit site", error);
            addNotification("Failed to edit the site", false);
          }
          setIsSubmitting(false);
        },
        onSettled: () => {
          setIsSubmitting(false);
        },
      }
    );
  };

  const sourceOptions = [
    "utility defined",
    "calculated",
    "Early stage projects",
    "Asset Registry",
    "COD Tracker",
    "SSI Constr Early Stage",
    "SSI Operational registry",
    "M&A operations",
    "utility fleet",
    "TransAlta operations",
    "ECHO Residential",
    "ASD Residential",
    "Deriva Energy",
    "X-ELIO",
    "BEP US",
  ];

  const polygonMapInitialCenter =
    formData.latitude && formData.longitude
      ? (() => {
          const lat = parseFloat(String(formData.latitude));
          const lng = parseFloat(String(formData.longitude));
          return !isNaN(lat) && !isNaN(lng) ? { lat, lng } : undefined;
        })()
      : undefined;

  const modalPaperSx = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "96%", sm: "92%" },
    maxWidth: EDIT_SITE_MODAL_MAX_WIDTH,
    maxHeight: `${EDIT_SITE_MODAL_VH}vh`,
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 1,
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          outline: "none",
        }}
      >
        <Box sx={modalPaperSx}>
          {isLoadingSiteData ||
          isLoadingSiteGeometry ||
          isLoadingPortfoliosData ? (
            // Loading message while data is being fetched
            <Typography variant="h6" sx={{ p: 4, textAlign: "center" }}>
              Loading site data...
            </Typography>
          ) : (
            <>
              <Paper
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingX: 4,
                  paddingY: 2,
                }}
              >
                {formData.sam_id ? (
                  <Typography
                    variant="h5"
                    sx={{
                      marginTop: 1,
                      fontWeight: "bold",
                    }}
                  >
                    {formData.sam_id}
                  </Typography>
                ) : (
                  <Typography
                    variant="h5"
                    sx={{
                      marginTop: 1,
                      fontWeight: "bold",
                    }}
                  >
                    EDIT SITE
                  </Typography>
                )}

                <IconButton onClick={onClose}>
                  <HighlightOffOutlinedIcon />
                </IconButton>
              </Paper>

              <Box
                sx={{
                  display: "flex",
                  flex: 1,
                  flexDirection: { xs: "column", md: "row" },
                  minHeight: 0,
                  alignItems: "stretch",
                  maxHeight: {
                    md: `calc(${EDIT_SITE_MODAL_VH}vh - ${EDIT_SITE_BODY_BELOW_HEADER_PX}px)`,
                  },
                }}
              >
                <Box
                  component="form"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    flex: { xs: "1 1 auto", md: "1 1 50%" },
                    minWidth: 0,
                    maxHeight: {
                      xs: "none",
                      md: `calc(${EDIT_SITE_MODAL_VH}vh - ${EDIT_SITE_FORM_SCROLL_TOP_PX}px)`,
                    },
                    overflowY: "auto",
                    p: 3,
                    pt: 2,
                  }}
                >
                  <TextField
                  label="Site Name *"
                  variant="outlined"
                  fullWidth
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                />
                <TextField
                  label="City"
                  variant="outlined"
                  fullWidth
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  error={!!formErrors.city}
                  helperText={formErrors.city}
                />
                <TextField
                  label="State"
                  variant="outlined"
                  fullWidth
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  error={!!formErrors.state}
                  helperText={formErrors.state}
                />
                <TextField
                  label="Address"
                  variant="outlined"
                  fullWidth
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  error={!!formErrors.address}
                  helperText={formErrors.address}
                />
                <TextField
                  label="Latitude"
                  variant="outlined"
                  fullWidth
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleChange}
                  error={!!formErrors.latitude}
                  helperText={formErrors.latitude}
                />
                <TextField
                  label="Longitude"
                  variant="outlined"
                  fullWidth
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleChange}
                  error={!!formErrors.longitude}
                  helperText={formErrors.longitude}
                />
                <TextField
                  select
                  label="Portfolio *"
                  variant="outlined"
                  fullWidth
                  name="portfolio"
                  value={formData.portfolio}
                  onChange={handleChange}
                  error={!!formErrors.portfolio}
                  helperText={formErrors.portfolio}
                >
                  {PortfoliosData?.map(
                    (portfolio: { id: number; name: string }) => (
                      <MenuItem key={portfolio.id} value={portfolio.id}>
                        {portfolio.name}
                      </MenuItem>
                    )
                  )}
                </TextField>
                <TextField
                  label="QF Docket ID"
                  variant="outlined"
                  fullWidth
                  name="docket_id"
                  value={formData.docket_id}
                  onChange={handleChange}
                  error={!!formErrors.docket_id}
                  helperText={formErrors.docket_id}
                />
                <TextField
                  label="Polygon (GeoJSON or WKT)"
                  variant="outlined"
                  fullWidth
                  name="polygon"
                  value={formData.polygon}
                  onChange={handleChange}
                  error={!!formErrors.polygon}
                  helperText={
                    formErrors.polygon ||
                    "Paste GeoJSON or WKT; the map preview updates as you type"
                  }
                />
                {!isWideLayout && (
                  <Box sx={{ pt: 0.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600 }}
                    >
                      Map preview
                    </Typography>
                    <PolygonMap
                      key={formData.polygon ? "has-polygon" : "empty"}
                      polygonData={formData.polygon}
                      siteName={formData.name}
                      mapContainerHeight="360px"
                      initialCenter={polygonMapInitialCenter}
                    />
                  </Box>
                )}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="DC kW *"
                    variant="outlined"
                    fullWidth
                    name="dc_kw"
                    value={formData.dc_kw}
                    onChange={handleChange}
                    error={!!formErrors.dc_kw}
                    helperText={formErrors.dc_kw}
                  />
                  <TextField
                    label="AC kW *"
                    variant="outlined"
                    fullWidth
                    name="ac_kw"
                    value={formData.ac_kw}
                    onChange={handleChange}
                    error={!!formErrors.ac_kw}
                    helperText={formErrors.ac_kw}
                  />
                </Box>
                <TextField
                  select
                  label="AC Source"
                  variant="outlined"
                  fullWidth
                  name="ac_source"
                  value={formData.ac_source}
                  onChange={handleChange}
                  error={!!formErrors.ac_source}
                  helperText={formErrors.ac_source}
                >
                  {sourceOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="DC Source"
                  variant="outlined"
                  fullWidth
                  name="dc_source"
                  value={formData.dc_source}
                  onChange={handleChange}
                  error={!!formErrors.dc_source}
                  helperText={formErrors.dc_source}
                >
                  {sourceOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                  }}
                >
                  <CustomButton
                    sx={{
                      paddingY: 0.5,
                    }}
                    variant="outlined"
                    color="secondary"
                    label=" Cancel"
                    onClick={onClose}
                  />

                  <CustomButton
                    disabled={isPending || isSubmitting}
                    sx={{
                      paddingY: 0.5,
                    }}
                    variant="contained"
                    color="primary"
                    label="Save Site"
                    onClick={handleSubmit}
                  />
                </Box>
                </Box>

                {isWideLayout && (
                  <Box
                    sx={{
                      flex: "0 0 46%",
                      minWidth: 340,
                      maxWidth: 560,
                      borderLeft: 1,
                      borderColor: "divider",
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? "rgba(255,255,255,0.04)"
                          : "grey.50",
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      minHeight: "min(52vh, 520px)",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600, flexShrink: 0 }}
                    >
                      Map preview
                    </Typography>
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                      <PolygonMap
                        key={formData.polygon ? "has-polygon" : "empty"}
                        polygonData={formData.polygon}
                        siteName={formData.name}
                        mapContainerHeight="min(52vh, 480px)"
                        initialCenter={polygonMapInitialCenter}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default EditNewSiteModal;
